import { $themeColors } from '@themeConfig'

/**
 * Dados e opções de gráficos do dashboard do Formulário de Relato
 * (mesma API / regras que reporting-channel/dashboard/Index.vue).
 */
export default {
  computed: {
    severityIndicators() {
      if (!this.dashboardData || !this.dashboardData.indicators) {
        return []
      }
      return this.dashboardData.indicators.filter(
        indicator => indicator.severity && indicator.total > 0
      )
    },
    chartSeries() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return []
      }
      return this.severityIndicators.map(indicator => {
        const total = indicator.total
        return typeof total === 'number' && !isNaN(total) ? total : 0
      })
    },
    chartLabels() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return []
      }
      return this.severityIndicators.map(indicator => {
        return (indicator.severity && indicator.severity.label) || 'Sem label'
      })
    },
    chartColors() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return []
      }
      return this.severityIndicators.map(indicator => {
        const colorName = indicator.severity && indicator.severity.color
        if (
          colorName &&
          typeof colorName === 'string' &&
          colorName.startsWith('#')
        ) {
          return colorName
        }
        const color = this.getColorForBadge(colorName)
        return color || '#5E50EE'
      })
    },
    totalFormularios() {
      if (!this.severityIndicators || this.severityIndicators.length === 0) {
        return 0
      }
      return this.severityIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    },
    typeReportingIndicators() {
      if (
        !this.dashboardData ||
        !this.dashboardData.type_reporting_indicators
      ) {
        return []
      }
      return this.dashboardData.type_reporting_indicators.filter(
        indicator => indicator.type_reporting && indicator.total > 0
      )
    },
    typeReportingChartSeries() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return []
      }
      return this.typeReportingIndicators.map(indicator => indicator.total)
    },
    typeReportingChartLabels() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return []
      }
      return this.typeReportingIndicators.map(
        indicator =>
          (indicator.type_reporting && indicator.type_reporting.name) ||
          'Sem nome'
      )
    },
    typeReportingChartColors() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return []
      }
      return this.typeReportingIndicators.map(indicator => {
        return this.getColorForTypeReporting(indicator.type_reporting.id)
      })
    },
    totalTypeReporting() {
      if (
        !this.typeReportingIndicators ||
        this.typeReportingIndicators.length === 0
      ) {
        return 0
      }
      return this.typeReportingIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    },
    statusIndicators() {
      if (!this.dashboardData || !this.dashboardData.status_indicators) {
        return []
      }
      return this.dashboardData.status_indicators
        .filter(indicator => indicator.status && indicator.total > 0)
        .map(indicator => {
          const color =
            (indicator.status && indicator.status.color) || '#5E50EE'
          return {
            ...indicator,
            status: {
              ...indicator.status,
              color
            },
            total_time_formatted: indicator.total_time_formatted || null
          }
        })
    },
    statusChartSeries() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return []
      }
      return this.statusIndicators.map(indicator => {
        const total = indicator.total
        return typeof total === 'number' && !isNaN(total) ? total : 0
      })
    },
    statusChartLabels() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return []
      }
      return this.statusIndicators.map(indicator => {
        return (indicator.status && indicator.status.label) || 'Sem label'
      })
    },
    statusChartColors() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return []
      }
      return this.statusIndicators.map(indicator => {
        const colorName =
          (indicator.status && indicator.status.color) || '#5E50EE'
        if (
          colorName &&
          typeof colorName === 'string' &&
          colorName.startsWith('#')
        ) {
          return colorName
        }
        const mappedColorName = colorName === 'grey' ? 'secondary' : colorName
        const color = this.getColorForBadge(mappedColorName)
        return color || '#5E50EE'
      })
    },
    totalStatus() {
      if (!this.statusIndicators || this.statusIndicators.length === 0) {
        return 0
      }
      return this.statusIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    },
    identificationIndicators() {
      if (
        !this.dashboardData ||
        !this.dashboardData.identification_indicators
      ) {
        return []
      }
      return this.dashboardData.identification_indicators.filter(
        indicator => indicator.total > 0
      )
    },
    identificationChartSeries() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return []
      }
      return this.identificationIndicators.map(indicator => {
        const total = indicator.total
        return typeof total === 'number' && !isNaN(total) ? total : 0
      })
    },
    identificationChartLabels() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return []
      }
      return this.identificationIndicators.map(indicator => {
        return indicator.identification_status || 'Sem status'
      })
    },
    identificationChartColors() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return []
      }
      return this.identificationIndicators.map(indicator => {
        return indicator.is_identify ? '#28C76F' : '#82868B'
      })
    },
    totalIdentification() {
      if (
        !this.identificationIndicators ||
        this.identificationIndicators.length === 0
      ) {
        return 0
      }
      return this.identificationIndicators.reduce((sum, indicator) => {
        const total = indicator.total
        const numTotal = typeof total === 'number' && !isNaN(total) ? total : 0
        return sum + numTotal
      }, 0)
    }
  },
  methods: {
    getColorForBadge(colorName) {
      if (colorName && colorName.startsWith('#')) {
        return colorName
      }
      const colorMap = {
        primary: $themeColors.primary || '#7367F0',
        info: $themeColors.info || '#00CFE8',
        warning: $themeColors.warning || '#FF9F43',
        danger: $themeColors.danger || '#EA5455',
        success: $themeColors.success || '#28C76F',
        dark: $themeColors.dark || '#4B4B4B',
        secondary: $themeColors.secondary || '#82868B',
        grey: $themeColors.secondary || '#82868B'
      }
      return colorMap[colorName] || $themeColors[colorName] || colorName
    },
    getColorForTypeReporting(typeId) {
      const colorPalette = [
        '#7367F0',
        '#28C76F',
        '#FF9F43',
        '#EA5455',
        '#00CFE8',
        '#82868B',
        '#9C27B0',
        '#FF6B6B',
        '#4ECDC4',
        '#45B7D1',
        '#FFA07A',
        '#98D8C8',
        '#F7DC6F',
        '#BB8FCE',
        '#85C1E2',
        '#F8B739',
        '#52BE80',
        '#EC7063',
        '#5DADE2',
        '#F1948A'
      ]
      if (!typeId) {
        return colorPalette[0]
      }
      let hash = 0
      const idString = String(typeId)
      for (let i = 0; i < idString.length; i++) {
        hash = (hash << 5) - hash + idString.charCodeAt(i)
        hash = hash & hash
      }
      const index = Math.abs(hash) % colorPalette.length
      return colorPalette[index]
    },
    /**
     * Donut compacto para o mural (ApexCharts).
     * @param {object} opts
     * @param {number[]} opts.series
     * @param {string[]} opts.labels
     * @param {string[]} opts.colors
     * @param {number} opts.total — valor no centro do donut
     * @param {string} [opts.centerLabel='Total']
     */
    homeCompactDonutOptions({
      series,
      labels,
      colors,
      total,
      centerLabel = 'Total'
    }) {
      const safeTotal =
        typeof total === 'number' && !isNaN(total) ? total : 0
      const denom = safeTotal > 0 ? safeTotal : 1
      const manySlices = series && series.length > 8
      return {
        chart: {
          type: 'donut',
          toolbar: { show: false },
          animations: { enabled: true }
        },
        labels: labels || [],
        colors: colors || [],
        legend: {
          show: true,
          position: 'bottom',
          fontSize: '11px',
          itemMargin: { horizontal: 6, vertical: 2 },
          markers: { width: 8, height: 8 }
        },
        dataLabels: {
          enabled: !manySlices,
          formatter: val =>
            typeof val === 'number' && !isNaN(val) ? val.toFixed(1) + '%' : ''
        },
        plotOptions: {
          pie: {
            donut: {
              size: '62%',
              labels: {
                show: true,
                name: { show: true, fontSize: '12px', fontWeight: 600 },
                value: { show: true, fontSize: '13px' },
                total: {
                  show: true,
                  label: centerLabel,
                  formatter: () => safeTotal
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter(val, { seriesIndex }) {
              const n =
                series && series[seriesIndex] !== undefined
                  ? Number(series[seriesIndex]) || 0
                  : 0
              const pct = ((n / denom) * 100).toFixed(1)
              return `${n} (${pct}%)`
            }
          }
        }
      }
    }
  }
}
